import React, { useEffect, useRef, useState } from "react";
import { T } from "../../app/tokens.js";
import { useEditablePanel } from "../../hooks/useEditablePanel.js";
import { parseNumericField } from "../../utils/parseNumericField.js";
import { SpacePolicyDefinitions } from "../../components/SpacePolicyDefinitions.jsx";
import { DEFINITION_FIELDS, definitionPolicy, validateThresholds } from "../../services/policyThresholds.js";
import { previewPolicyBaseline, putPolicyBaseline } from "../../data/policyClient.js";
import { refreshSpacePolicy } from "../../services/spacePolicyStore.js";

const LABELS = {
  under_used_pct: "Under-Used below (%)", space_fit_tight_pct: "Tight from (%)",
  density_floor_sf: "Floor (sf per seat)", outlook_near_limit_pct: "Near limit from (%)",
};
function CardControls({ card, baseline, isAdmin, onSaved }) {
  const panel = useEditablePanel(() => {
    const p = definitionPolicy(baseline);
    const resolved = { under_used_pct: p.underUsedPct, space_fit_tight_pct: p.tightPct,
      density_floor_sf: p.sfFloor, outlook_near_limit_pct: p.nearLimitPct };
    return { ...Object.fromEntries(DEFINITION_FIELDS[card].map(k => [k, String(resolved[k])])),
      effective_from: new Date().toISOString().slice(0, 10) };
  });
  const [preview, setPreview] = useState(null), [error, setError] = useState(null), [busy, setBusy] = useState(false);
  const generation = useRef(0);
  const baselineKey = JSON.stringify(baseline);
  useEffect(() => { generation.current++; setPreview(null); }, [baselineKey]);
  if (!isAdmin) return null;
  const change = (key, value) => {
    generation.current++; setPreview(null); setError(null); panel.setField(key, value);
  };
  const cancel = () => { generation.current++; panel.reset(); setPreview(null); setError(null); };
  const request = () => {
    const knobs = Object.fromEntries(DEFINITION_FIELDS[card].map(k => [k, parseNumericField(panel.draft[k])]));
    if (Object.values(knobs).some(v => v == null)) throw new Error("Enter a finite number in every field.");
    const errors = validateThresholds({ ...baseline, ...knobs });
    if (errors._form) throw new Error(errors._form);
    return { card, knobs, effective_from: panel.draft.effective_from };
  };
  const run = async save => {
    const version = ++generation.current;
    setBusy(true); setError(null);
    try {
      const payload = request();
      if (save) {
        if (!preview) throw new Error("Preview the impact before saving.");
        await putPolicyBaseline({ ...payload, preview_token: preview.token });
        panel.reset(); setPreview(null);
        onSaved();
        await refreshSpacePolicy();
      } else {
        setPreview(null);
        const result = await previewPolicyBaseline(payload);
        if (!result?.token || !Number.isInteger(result.officeCount) || !Number.isInteger(result.marketCount)
          || !Array.isArray(result.offices)) throw new Error("Impact preview is unavailable. Try again before saving.");
        if (version === generation.current) setPreview(result);
      }
    } catch (err) { if (version === generation.current) { setPreview(null); setError(err.message); } }
    finally { setBusy(false); }
  };
  return <div data-testid={`${card}-definition-controls`} style={{ marginBottom: 14 }}>
    <p>Effective {baseline?.effectiveFrom ?? "documented defaults"}</p>
    {error && <p role="alert" style={{ color: T.red }}>{error}</p>}
    {!panel.editing ? <button type="button" onClick={() => { setError(null); panel.setEditing(true); }}>Edit</button> : <>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        {DEFINITION_FIELDS[card].map(key => <label key={key}>
          {LABELS[key]}<input aria-label={LABELS[key]} type="number" step="any" disabled={busy}
            value={panel.draft[key]} onChange={e => change(key, e.target.value)} style={{ display: "block", width: 140 }} />
        </label>)}
        <label>Effective date<input aria-label={`${card} effective date`} type="date" disabled={busy}
          max={new Date().toISOString().slice(0, 10)} value={panel.draft.effective_from}
          onChange={e => change("effective_from", e.target.value)} style={{ display: "block" }} /></label>
      </div>
      {preview && <div role="status">
        <p>This moves {preview.officeCount} offices and {preview.marketCount} markets</p>
        <p>{preview.scope}</p>{preview.note && <p>{preview.note}</p>}
        <ul>{preview.offices.slice(0, 5).map(o => <li key={o.id}>{o.name} ({o.id}): {o.oldBand} → {o.newBand}</li>)}</ul>
        {preview.offices.length > 5 && <p>And {preview.offices.length - 5} more offices.</p>}
      </div>}
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button type="button" disabled={busy} onClick={() => run(false)}>{busy ? "Working…" : "Preview impact"}</button>
        <button type="button" disabled={busy || !preview} onClick={() => run(true)}>Save</button>
        <button type="button" disabled={busy} onClick={cancel}>Cancel</button>
      </div>
    </>}
  </div>;
}
export function DefinitionPolicyCards({ baseline, isAdmin, onSaved }) {
  return <SpacePolicyDefinitions policy={baseline ? definitionPolicy(baseline) : null}
    fitControls={<CardControls card="fit" {...{ baseline, isAdmin, onSaved }} />}
    outlookControls={<CardControls card="outlook" {...{ baseline, isAdmin, onSaved }} />} />;
}
